import {
    CENTRA_LOCALE_SHIPPING_COUNTRY_CODES,
    isCentraLocaleShippingCountryCode
} from "@/data/centra-locale-shipping-country-code"
import { SHIPPING_COUNTRIES } from "@/data/shipping-countries"
import { SHIPPING_COUNTRY_CODES } from "@/data/shipping-country-code"
import { CentraLanguageCode, validateLanguageCode } from "@/data/supported-languages"
import { CENTRA_MARKET_CODE, CentraMarketCode } from "@/data/supported-markets"
import centraPath from "@/lib/centra-path/centra-path-init"
import logger from "@/lib/logger/logger-init"
import { mapCentraShippingCountryCodeToCentraMarketCode } from "@/maps/map-utils"
import { GeneralLink } from "@/types"

/**
 * Retrieves the language code from a locale string and validates it.
 *
 * @param {string} locale The locale string to extract the language code from.
 * @returns {CentraLanguageCode | undefined} A valid language code if the extraction and validation succeed,
 *          otherwise undefined.
 *
 * This function extracts the language code part (before the '-') from a given locale string, then validates it
 * against a predefined set of valid codes. If the language code is valid, it returns the code; otherwise, it logs
 * an error and returns undefined. This can be useful for applications that need to handle different languages
 * and ensure that they work with valid language codes.
 *
 * `CentraLanguageCode` is assumed to be a TypeScript type representing valid language codes for the application.
 *
 * `validateLanguageCode` is assumed to be a function that checks if the given code is valid and returns an object
 * with a `success` boolean property and a `data` property containing the validated `CentraLanguageCode`.
 */

export function getLangCode(locale: string): CentraLanguageCode | undefined {
    const code = locale.split("-")[0]
    const validation = validateLanguageCode(code)

    if (validation.success) {
        return validation.data
    } else {
        logger.error("Invalid language code: ", code)

        return undefined
    }
}

/**
 * Generates an href value for a given GeneralLink object.
 *
 * @param {GeneralLink} link The link object containing properties that dictate the href's structure.
 * @returns {string | undefined} The constructed href based on the link object properties, or undefined if no valid properties are provided.
 *
 * The function supports constructing hrefs for different link types: external URLs, internal paths,
 * category URIs, and product display URIs. It prioritizes these properties in the order listed.
 * If the link is external, it returns the external URL directly. For internal links, category URIs,
 * and product display URIs, it ensures the returned href starts with a slash ("/"). If none of these
 * properties are defined, it returns undefined.
 *
 * Example usage:
 *
 * const link = { external: "https://example.com" };
 * const href = getGeneralLinkHref(link);
 * // Returns 'https://example.com'
 */

export function getGeneralLinkHref(link: GeneralLink) {
    const href =
        link.external ||
        link.internal ||
        (link.categoryUri && `/categories/${link.categoryUri}`) ||
        (link.productDisplayUri && `/product/${link.productDisplayUri}`) ||
        undefined

    // Ensure href is a string before proceeding.
    if (typeof href === "undefined" || typeof href !== "string") return href

    if (link.external) return href

    return href.startsWith("/") ? href : `/${href}`
}

/**
 * Checks if a GeneralLink object has at least one of the expected properties defined.
 *
 * @param {GeneralLink | null | undefined} link The GeneralLink object to check.
 * @returns {boolean} True if the link object has at least one of the properties (external, internal, categoryUri, productDisplayUri) defined; otherwise, false.
 *
 * This function is useful for determining whether a GeneralLink object is effectively "empty"
 * or if it contains meaningful data that can be used to generate a link href.
 * It helps in scenarios where link objects might be optional or may come from dynamic sources,
 * ensuring that only links with actionable data are processed further.
 *
 * Example usage:
 *
 * const link = { internal: "/home" };
 * const isDefined = isGeneralLinkDefined(link);
 * // Returns true
 */

export function isGeneralLinkDefined(link?: GeneralLink | null): boolean {
    return (
        link?.external != null ||
        link?.internal != null ||
        link?.categoryUri != null ||
        link?.productDisplayUri != null
    )
}

/**
 * Returns a locale code that can be used when fetching data from Depict.
 * @param {string} languageCode
 */
export const getDepictLangCode = (languageCode: string) => {
    const mapping: Record<string, string> = {
        de: "de_DE",
        en: "en_GB",
        fr: "fr_FR",
        ja: "ja_JP"
    }
    return mapping[languageCode.toLowerCase()] || mapping.en
}
/**
 * Returns a market code that can be used when fetching data from Depict.
 * @param {string} countryCode
 */
export const getDepictMarketCode = (countryCode: string) => {
    const depictCountryToPricelist: Record<string, string> = {
        at: "at",
        au: "au",
        be: "be",
        bg: "bg",
        ca: "ca",
        ch: "ch",
        cy: "cy",
        cz: "cz",
        de: "de",
        dk: "dk",
        ee: "ee",
        es: "es",
        fi: "fi",
        fr: "fr",
        gb: "gb",
        gi: "gi",
        gr: "gr",
        hr: "hr",
        hu: "hu",
        ie: "ie",
        it: "it",
        jp: "jp",
        lt: "lt",
        lu: "lu",
        lv: "lv",
        mt: "mt",
        nl: "nl",
        no: "no",
        nz: "nz",
        pl: "pl",
        pt: "pt",
        ro: "ro",
        se: "se",
        si: "si",
        sk: "sk",
        us: "us"
    }
    return depictCountryToPricelist[countryCode.toLowerCase()] || "de"
}

/**
 * Extracts and validates a Centra market code from a Centra locale string.
 *
 * @param {string} centraLocale The Centra locale string, in the format of '[language-code]-[centra-locale-shipping-country-code]'.
 * @returns {CentraMarketCode | undefined} A valid market code if the extraction and validation succeed,
 *          otherwise undefined.
 *
 * This function is designed to parse a Centra locale string, and return the corresponding Centra market code.
 *
 * Returns "EU" as default market code if no market code is found for the given Centra shipping country code.
 */
export const getMarketCodeFromCentraLocale = (centraLocale: string): CentraMarketCode => {
    if (centraPath.isStringEuroCurrencyCentraLocale(centraLocale)) {
        return CENTRA_MARKET_CODE.Eu
    }

    const centraLocaleShippingCountryCode = centraLocale.split("-")[1]

    if (
        !isCentraLocaleShippingCountryCode(
            centraLocaleShippingCountryCode,
            CENTRA_LOCALE_SHIPPING_COUNTRY_CODES
        )
    ) {
        console.error(
            `Invalid Centra locale shipping country code: ${centraLocaleShippingCountryCode}. Returning EU as default market code.`
        )

        return CENTRA_MARKET_CODE.Eu
    }

    const centraMarket = mapCentraShippingCountryCodeToCentraMarketCode(
        centraLocaleShippingCountryCode
    )

    return centraMarket
}

// Utility function to capitalize each word
export const capitalizeWords = (str: string): string => {
    if (typeof str !== "string") {
        return ""
    }
    return str
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ")
}

/**
 * Retrieves the country name based on a given locale string.
 *
 * @param {string} locale - A locale string in the format "language-country" (e.g., "en-se").
 * @returns {string | undefined} - The country name corresponding to the country code, or undefined if not found.
 *
 * This function extracts the country code from the locale string and matches it
 * to the appropriate country name in the SHIPPING_COUNTRY_CODES object.
 *
 * Example usage:
 * const country = getCountryByCode("en-se", true);
 * const country = getCountryByCode("en-se");
 * // Returns: "Sweden"
 * // Returns: "sweden"
 */

export const getCountryByCode = (locale: string, capitalize: boolean): string => {
    if (!locale || locale.length < 2) {
        logger.error(`No locale provided: ${locale}`)
        return ""
    }

    if (locale === "en" || locale === "de") {
        return ""
    }

    let countryKey: string

    if (locale.length === 2) {
        countryKey =
            Object.keys(SHIPPING_COUNTRY_CODES).find(
                key =>
                    SHIPPING_COUNTRY_CODES[key as keyof typeof SHIPPING_COUNTRY_CODES] ===
                    locale
            ) || ""
    } else {
        const paramsLocale = locale.split("-")[1]
        countryKey =
            Object.keys(SHIPPING_COUNTRY_CODES).find(
                key =>
                    SHIPPING_COUNTRY_CODES[key as keyof typeof SHIPPING_COUNTRY_CODES] ===
                    paramsLocale
            ) || ""
    }

    if (countryKey) {
        const countryName =
            SHIPPING_COUNTRIES[countryKey as keyof typeof SHIPPING_COUNTRIES]
        if (capitalize) {
            return capitalizeWords(countryName)
        } else {
            return countryName
        }
    }

    return ""
}

// The max width that a downloaded source image should have if the image is full screen width.
// The value chosen is arbitrary and is just guess on what's the best tradeoff between size vs quality
// Increase number to better support large screens at the cost of performance
// Decrease to improve image load time for large screens at the cost of performance
export const IMAGE_FULLWIDTH_SIZE_PX = 2560
export const IMAGE_MOBILE_SIZE_PX = 1440

/**
 * Generates a set of localized paths for multiple languages based on an optional parameter.
 *
 * @param {string | undefined} params - An optional string representing a path segment (e.g., a product slug or category name).
 * @returns {Record<string, string>} An object where each key is a language-region code (e.g., "en-se", "de") and the value is a URL path for that locale.
 *
 * This function dynamically constructs language-specific URL paths. If a `params` value is provided,
 * it appends the parameter as a path segment for each locale. If no `params` is provided, the base URL for each locale is returned.
 *
 * Useful for generating hreflang links, localized URLs, or alternate language paths in applications with internationalized content.
 *
 * Example usage:
 *
 * const localizedPaths = languages("product/awesome-item");
 * // Returns:
 * // {
 * //   "x-default": "/en/product/awesome-item",
 * //   "de": "/de/product/awesome-item",
 * //   "fr": "/fr/product/awesome-item",
 * //   "en-se": "/en-se/product/awesome-item",
 * //   ...
 * // }
 *
 * const basePaths = languages();
 * // Returns:
 * // {
 * //   "x-default": "/en",
 * //   "de": "/de",
 * //   "fr": "/fr",
 * //   "en-se": "/en-se",
 * //   ...
 * // }
 */

type HreflangKeys =
    | "en"
    | "de"
    | "fr"
    | "en-cz"
    | "en-dk"
    | "en-no"
    | "en-pl"
    | "en-se"
    | "en-ch"
    | "de-ch"
    | "fr-ch"
    | "en-gb"
    | "en-au"
    | "en-nz"
    | "en-us"
    | "fr-ca"
    | "en-ca"
    | "ja"
    | "en-jp"

type HreflangMap = Record<HreflangKeys | "x-default", string>

export const generateHreflangs = (params?: string): HreflangMap => {
    const siteUrl = process.env.NEXT_PUBLIC_SITEMAP_DOMAIN || "https://localhost:3000"
    const path = params ? `/${params}` : ""

    const hreflangs: Record<HreflangKeys, string> = {
        en: `${siteUrl}/en${path}`,
        de: `${siteUrl}/de${path}`,
        fr: `${siteUrl}/fr${path}`,
        "en-cz": `${siteUrl}/en-cz${path}`,
        "en-dk": `${siteUrl}/en-dk${path}`,
        "en-no": `${siteUrl}/en-no${path}`,
        "en-pl": `${siteUrl}/en-pl${path}`,
        "en-se": `${siteUrl}/en-se${path}`,
        "en-ch": `${siteUrl}/en-ch${path}`,
        "de-ch": `${siteUrl}/de-ch${path}`,
        "fr-ch": `${siteUrl}/fr-ch${path}`,
        "en-gb": `${siteUrl}/en-gb${path}`,
        "en-au": `${siteUrl}/en-au${path}`,
        "en-nz": `${siteUrl}/en-nz${path}`,
        "en-us": `${siteUrl}/en-us${path}`,
        "fr-ca": `${siteUrl}/fr-ca${path}`,
        "en-ca": `${siteUrl}/en-ca${path}`,
        ja: `${siteUrl}/ja-jp${path}`,
        "en-jp": `${siteUrl}/en-jp${path}`
    }

    return {
        ...hreflangs,
        "x-default": `/` // force root for x-default
    }
}
